import { useFeatureValue } from '@growthbook/growthbook-react';
import { CardGiftcard, LinkedIn } from '@mui/icons-material';
import { Box, Theme, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { useSelector } from 'react-redux';

import { ComplianceTypes, Modals, urls } from '@/constants';
import { Link } from '@/containers';
import { DownloadAppIcon } from '@/icons';
import { nasbaLogo } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectSessionUserId,
  selectUserCompliance,
  selectUserRegion,
  uiActions,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { appStyles } from '@/theme';

interface AppFooterProps {
  showBorder?: boolean;
  compact?: boolean;
  backgroundColor?: string;
}

export const AppFooter = ({
  showBorder = true,
  compact = false,
  backgroundColor = 'white',
}: AppFooterProps) => {
  const region = useAppSelector(selectUserRegion);
  const compliance = useSelector(selectUserCompliance);

  const userId = useAppSelector(selectSessionUserId);
  const isShareWithAFriendEnabled =
    useFeatureValue('share-with-a-friend', false) && !!userId;

  const isDownloadLinkEnabled =
    useFeatureValue('internal-download-app-nav-item', false) && !!userId;

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const dispatch = useAppDispatch();

  const footerSections = [
    {
      label: 'About Us',
      link: urls.about[region],
    },
    {
      label: 'Terms & Conditions',
      link: urls.lumiqTC,
    },
    {
      label: 'Privacy Policy',
      link: urls.lumiqPrivacy[region],
    },
    {
      label: 'Contact Us',
      onClick: () => window.Intercom?.('show'),
    },
    {
      label: 'FAQ',
      link: urls.faq,
    },
  ];

  return (
    <Box
      sx={[
        styles.container,
        showBorder && styles.containerBorder,
        !compact && styles.containerPadding,
        { pb: compact ? 0 : 4, backgroundColor },
      ]}
      component="footer">
      <Box sx={appStyles.limitPageWidth}>
        <Box sx={styles.rowContainer}>
          <Box
            sx={[
              styles.sectionContainer,
              {
                '&:empty': { display: 'none' },
                display: 'flex',
                gap: compact ? 1 : 2,
                mb: { xs: 2, sm: 1 },
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                  md: 'column',
                  lg: 'row',
                },
              },
            ]}>
            {isShareWithAFriendEnabled && (
              <Link
                onClick={() => {
                  dispatch(
                    uiActions.setActiveModal({
                      name: Modals.SHARE_WITH_A_FRIEND,
                    }),
                  );
                }}
                aria-label="Share with a friend"
                color="text.main">
                <Box sx={styles.actionBox}>
                  <CardGiftcard sx={{ mr: 0.5 }} />
                  Share with a friend
                </Box>
              </Link>
            )}
            {isDownloadLinkEnabled && (
              <Link
                onClick={() => {
                  AnalyticsService.buttonClick({
                    name: EVENT_CONSTANTS.BUTTON.DOWNLOAD_APP,
                  });

                  if (isMobile) {
                    window.open(urls.downloadApp);
                    return;
                  }

                  dispatch(
                    uiActions.setActiveModal({ name: Modals.APP_DOWNLOAD }),
                  );
                }}
                aria-label="Mobile app"
                color="text.main">
                <Box sx={styles.actionBox}>
                  <DownloadAppIcon />
                  Mobile app
                </Box>
              </Link>
            )}
          </Box>
          <Box sx={[styles.sectionContainer, { gap: compact ? 2 : 5 }]}>
            {footerSections.map(item => (
              <Box key={item.label}>
                <Link
                  to={item.link}
                  onClick={item.onClick}
                  target={item.link ? '_blank' : undefined}
                  rel={item.link ? 'noopener noreferrer' : undefined}
                  aria-label={`${item.label}${item.link ? ' (opens in new tab)' : ''}`}
                  color="text.main">
                  {item.label}
                </Link>
              </Box>
            ))}
            {socialSections.map(({ label, link, Icon }) => (
              <Link
                key={label}
                to={link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${label} (opens in new tab)`}
                color="text.main">
                <Box sx={styles.iconLabel}>
                  <Icon sx={styles.icon} />
                  {label}
                </Box>
              </Link>
            ))}
          </Box>
        </Box>
        <Box>
          {compliance === ComplianceTypes.CPE && (
            <Box sx={styles.nasbaContainer}>
              <Box
                component="img"
                src={nasbaLogo}
                sx={styles.nasbaLogo}
                alt="nasba logo"
              />
              <Box sx={styles.nasbaTextGroup}>
                <Typography variant="caption1">
                  LumiQ is registered with the National Association of State
                  Boards of Accountancy (NASBA) as a sponsor of continuing
                  professional education on the National Registry of CPE
                  Sponsors. State boards of accountancy have the final authority
                  on the acceptance of individual courses for CPE credit.
                  Complaints regarding registered sponsors may be submitted to
                  the National Registry of CPE Sponsors through its website:{' '}
                  <Link to="https://www.nasbaregistry.org/" inline>
                    <Typography variant="caption1">
                      www.nasbaregistry.org
                    </Typography>
                  </Link>
                </Typography>

                <Typography variant="caption1">
                  LumiQ Inc. Sponsor ID Numbers: NASBA (146039), New York
                  (002996), Texas (010643)
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const socialSections = [
  {
    Icon: LinkedIn,
    label: 'LinkedIn',
    link: urls.social.linkedin,
  },
];

const styles = {
  container: (theme: Theme) => ({
    mt: 20,
    pt: 3,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      mt: 10,
      marginBottom: 0,
    },
    position: 'relative',
    zIndex: 'drawer',
    backgroundColor: 'white',
  }),
  containerPadding: (theme: Theme) => ({
    px: theme.dimensions.appLayout.px,
  }),
  containerBorder: {
    borderTop: '1px solid',
    borderColor: 'grey50',
  },
  rowContainer: (theme: Theme) => ({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 1,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      mb: 8,
    },
  }),
  sectionContainer: (theme: Theme) => ({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    mb: 5,
    [theme.breakpoints.down(660)]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3,
    },
  }),
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    svg: { color: (theme: Theme) => theme.palette.secondary.main },
    fontWeight: 'bold',
    mr: { xs: 2, sm: 0 },
  },
  icon: {
    width: 24,
    height: 24,
    color: 'grey100',
  },
  iconLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  nasbaContainer: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 3,
    mb: 10,
    p: 2,
    border: '1px solid',
    borderColor: 'grey50',
    maxWidth: '1400px',
    mx: 'auto',
    borderRadius: 5,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
  nasbaTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  nasbaLogo: {
    width: 68,
    height: 87,
  },
};
